// 体幹（Posture）の自然回復ロジック。
// スタン/よろけ中は回復しない。
// ボス側・プレイヤー側ともにHP残量に比例して鈍る (SEKIRO本家のHP↔体幹回復カップリング。
//   体力が高いほど体幹回復が速い=振り払う。 パリィ/弾きの威力自体は減らさない)。

import type { Boss, Player } from './types'
import { BOSS, FIXED_DT, PLAYER } from './balance'
import { playerMaxHp } from './difficulty'
import { bossPostureRecoveryMult } from './combat'
import { getEconPreset, bossPostureRecoveryPerSec, bossPostureGraceSec } from './econ'

// プレイヤーHP率に連動した体幹回復倍率 (SEKIRO表を滑らかな線形近似: 満タン=1.0 / 瀕死=floorMul)。
function playerPostureRecoveryMult(player: Player): number {
  const maxHp = playerMaxHp()
  const frac = maxHp > 0 ? Math.max(0, Math.min(1, player.hp / maxHp)) : 1
  const floor = PLAYER.postureRecoveryFloorMul
  return floor + (1 - floor) * frac
}

export function regenPlayerPosture(player: Player): Player {
  // 🔵 2026-09-04 監査: ガード中もタイマーが減っていたため、長押しし続ける相手に 2.5 秒ロックが
  //   効いていなかった (罰の向きが逆＝受け続ける人にペナルティがゼロ、1発だけ受けてすぐ攻めに戻る
  //   人だけが2.5秒を丸ごと払う、という狙いと逆の挙動になっていた)。ガード中は凍結する。
  //   stunned 中は今までどおり減らす (stun 明けにすぐ回復できるよう)。
  const lockSec = player.state === 'guarding'
    ? player.postureRegenLockSec
    : Math.max(0, player.postureRegenLockSec - FIXED_DT)
  // 気絶中・ガード中・回復停止中は体幹を回復しない。
  //   guarding = パリィ長押しガード (トグル・既定ON・2026-07-20〜。`__guard(false)` で A/B 可): ガードを張っている限り自然回復を止める
  //   (spec 5「体幹の自然回復はガード中停止」)。 フラグOFFでは 'guarding' に入らない=従来挙動不変。
  if (player.state === 'stunned' || player.state === 'guarding' || lockSec > 0) {
    return { ...player, postureRegenLockSec: lockSec }
  }
  // ②本物のSEKIRO化 (2026-07-16 Part3): 回復レートをHP率でスケール
  //   (満タン→今より速い/瀕死→今より遅い。 §「通常弾きの罰を緩める」の片翼)。
  const rate = PLAYER.postureRecoveryPerSec * playerPostureRecoveryMult(player)
  const next = player.posture - rate * FIXED_DT
  return { ...player, posture: Math.max(0, next), postureRegenLockSec: 0 }
}

export function regenBossPosture(boss: Boss): Boss {
  if (boss.state === 'staggered') return boss
  // ロック用タイマーは引き続き減衰させる (v2 econ の bossPostureRegenLockSec は②で0=事実上不使用だが、
  //   フィールド自体は残す)。
  // 🔴 2026-07-29: **猶予 (grace) を復活させた。**
  //   猶予 = 弾いた/当てた直後、体幹の減衰が止まっている時間 (ボスごとの摘み = econ の
  //   bossPools[].postureRegenLockSec、既定は bossPostureRegenLockSec)。
  //   なぜ必要か: 連撃を持たないボス (王=単発2本 / 騎士=弾ける単発1本) は、拍と拍の間に
  //   減衰が走るため**構造的に体幹が積めなかった** (実測 economy_probe)。猶予があれば拍を跨げる。
  //   ⚠️ 崖は作らない (ユーザー裁定「5秒で急にゼロは理不尽」)。猶予が切れた後は
  //      BOSS.postureDecayRampSec かけて 0→全速へ**なめらかに立ち上げる**。
  //   ⚠️ 貯金にしない (ユーザー裁定「5回弾いて1分回避して最後の1回で倒せるのはおかしい」)。
  //      猶予は「次の一撃を跨ぐ」長さに収める (0.4〜1.5秒目安)。
  //   実装メモ: 猶予切れ後の経過時間を別フィールドを増やさずに持つため、タイマーを
  //      **負の側へ -ramp までだけ流す**。負の絶対値 = 猶予が切れてからの秒数。
  //   ⚠️ 猶予0のボス (= v1 旧経済) では立ち上がりも掛けない。掛けると「猶予を使っていない
  //      経済」まで減衰が鈍り、v1=従来挙動不変 という約束が静かに破れる (2026-07-30 修正)。
  const ramp = bossPostureGraceSec(boss.id) > 0 ? Math.max(0.0001, BOSS.postureDecayRampSec) : 0
  //   ⚠️ 下限は ramp>0 のときだけ負にする。`-ramp` は ramp=0 だと -0 になり、
  //      「タイマーは0のまま」という約束が Object.is(-0, 0)=false で静かに破れる。
  const lockFloor = ramp > 0 ? -ramp : 0
  const lockSec = Math.max(lockFloor, (boss.postureRegenLockSec ?? 0) - FIXED_DT)
  // blink中は攻撃もパリィも不可=lockを更新できないため、v2では回復も凍結 (2026-07-04)。
  //   ②で bossPostureRegenLockSec が0になっても blink凍結自体は維持する (プリセットで判定)。
  if ((boss.state === 'blink' || boss.state === 'reappear') && getEconPreset() === 'v2') {
    return { ...boss, postureRegenLockSec: lockSec }
  }
  // ②本物のSEKIRO化 (2026-07-16 Part2): 「ゲイン後は回復停止」の壁を廃止し、常に
  //   幕HP率スケールのレートで回復させる (満タンHP→速い=振り払う / 低HP→ほぼ止まる=削るほど実る)。
  // 猶予中 (lockSec > 0) は減衰ゼロ。切れたら ramp 秒かけて全速へ立ち上げる。
  const decayScale = lockSec > 0 ? 0 : (ramp > 0 ? Math.min(1, -lockSec / ramp) : 1)
  // ★2026-07-30: 減る速さはボス単位 (未指定は全ボス共通の既定)。ユーザー裁定=案2。
  const rate = bossPostureRecoveryPerSec(boss.id) * bossPostureRecoveryMult(boss) * decayScale
  const next = boss.posture - rate * FIXED_DT
  return { ...boss, posture: Math.max(0, next), postureRegenLockSec: lockSec }
}
